import ksBridge from '@ks/ks-bridge';
import { openOrDownload } from '@ks/web-util/open-app';
import { HOME_PAGE_URL } from '@/configs/constants';
import { debugConsole } from '@aio/utils';

/**
 * 验证URL的安全性
 * @param url - 要验证的URL
 * @returns 是否为安全的URL
 */
const isValidUrl = (url: string): boolean => {
  try {
    // 检查是否为字符串且不为空
    if (typeof url !== 'string' || !url.trim()) {
      return false;
    }

    // 创建URL对象进行基本验证
    const urlObj = new URL(url);

    // 只允许安全的协议（白名单机制）
    const allowedProtocols = ['http:', 'https:', 'kwai:'];
    if (!allowedProtocols.includes(urlObj.protocol)) {
      return false;
    }

    // 额外的安全检查：确保kwai协议的格式正确
    if (
      urlObj.protocol === 'kwai:' &&
      !urlObj.pathname &&
      !urlObj.search &&
      !urlObj.hash
    ) {
      // kwai协议应该有具体的路径或参数
      return false;
    }

    return true;
  } catch (error) {
    // URL格式无效或解析失败
    return false;
  }
};

/**
 * 清理URL输入
 * @param url - 原始URL
 * @returns 清理后的URL
 */
const sanitizeUrl = (url: string): string => {
  if (typeof url !== 'string') {
    return '';
  }

  // 使用更安全的方式清理URL
  // 1. 移除潜在的危险字符
  let cleaned = url.replace(/[<>"'`]/g, '');

  // 2. 移除首尾空白字符
  cleaned = cleaned.trim();

  // 3. 防止常见的XSS payload
  cleaned = cleaned.replace(/\s*javascript\s*:/gi, '');
  cleaned = cleaned.replace(/\s*data\s*:/gi, '');
  cleaned = cleaned.replace(/\s*vbscript\s*:/gi, '');

  return cleaned;
};

/**
 * 安全地打开或下载URL
 * @param url - 要处理的URL
 */
export const openOrDownloadUrl = (url: string): void => {
  try {
    // 输入验证
    if (!url || typeof url !== 'string') {
      debugConsole('openOrDownloadUrl: Invalid URL input');
      return;
    }

    // 清理URL
    const sanitizedUrl = sanitizeUrl(url);
    if (!sanitizedUrl) {
      debugConsole('openOrDownloadUrl: Sanitized URL is empty');
      return;
    }

    debugConsole('openOrDownloadUrl', sanitizedUrl);

    // 验证URL安全性
    if (!isValidUrl(sanitizedUrl)) {
      debugConsole('openOrDownloadUrl: Invalid or unsafe URL blocked');
      return;
    }

    if (ksBridge.sniff.isInApp) {
      if (sanitizedUrl.includes('kwai://')) {
        // 验证kwai协议的安全性
        try {
          const urlObj = new URL(sanitizedUrl);
          // 确保kwai协议有有效的路径
          if (urlObj.pathname || urlObj.search || urlObj.hash) {
            window.location.href = sanitizedUrl;
          } else {
            debugConsole('openOrDownloadUrl: Invalid kwai protocol format');
          }
        } catch (error) {
          debugConsole('openOrDownloadUrl: Failed to navigate to URL', error);
        }
        return;
      }

      // 使用try-catch包装ksBridge调用
      try {
        ksBridge.loadUrlOnNewPage({
          url: sanitizedUrl,
          type: 'back'
        });
      } catch (error) {
        debugConsole(
          'openOrDownloadUrl: ksBridge.loadUrlOnNewPage failed',
          error
        );
      }
      return;
    }

    // 非App环境下的处理
    if (sanitizedUrl.includes('kwai://')) {
      try {
        openOrDownload(sanitizedUrl);
      } catch (error) {
        debugConsole(
          'openOrDownloadUrl: openOrDownload kwai protocol failed',
          error
        );
      }
      return;
    }

    // 构造scheme URL
    try {
      const schemeUrl = `kwai://webview?url=${encodeURIComponent(
        sanitizedUrl
      )}&hideToolBar=true`;
      openOrDownload(schemeUrl);
    } catch (error) {
      debugConsole('openOrDownloadUrl: Failed to construct scheme URL', error);
    }
  } catch (error) {
    debugConsole('openOrDownloadUrl: Unexpected error', error);
  }
};

export const openApp = () => {
  if (!ksBridge.sniff.isInApp) {
    openOrDownload(HOME_PAGE_URL);
    return true;
  }
  return false;
};

export default openOrDownloadUrl;
